/**
 * Hisaab bootstrap. Registers components, wires gate -> app, paints marginalia.
 */
import './components/gate.ts';
import './components/tier-card.ts';
import './components/damage-pie.ts';
import './components/monthly-bars.ts';
import './components/category-rank.ts';
import './components/merchant-rank.ts';
import './components/impulse-spotlight.ts';
import './components/tx-feed.ts';
import './components/share-card.ts';
import './components/genius-bar.ts';
import './components/pay-cta.ts';
import './components/consent-bar.ts';
import './components/connect-section.ts';

import { store } from './store.ts';
import { loadSummary } from './storage.ts';
import { decryptJSON } from './crypto.ts';
import { formatINR, MERCHANT_DISPLAY } from './types.ts';
import type { Summary, Transaction } from './types.ts';
import './sync/sync-v2.ts';

async function bootstrap(): Promise<void> {
  const cached = await loadSummary();
  if (cached) {
    store.patch({ phase: 'ready', summary: cached });
    revealApp();
    refreshHero(cached);
    refreshMarginalia(cached);
    return;
  }

  const hashPasscode = location.hash.slice(1).trim();
  const sessionPasscode = sessionStorage.getItem('hisaab:passcode') ?? '';
  const passcode = hashPasscode || sessionPasscode;
  if (passcode) {
    try {
      const summary = await fetchAndDecrypt(passcode);
      store.patch({ phase: 'ready', summary });
      revealApp();
      refreshHero(summary);
      refreshMarginalia(summary);
      return;
    } catch {
      // fall through to gate
    }
  }
  store.patch({ phase: 'gate' });
  showGate();
}

async function fetchAndDecrypt(passcode: string): Promise<Summary> {
  const r = await fetch('/data.encrypted.json', { cache: 'no-cache' });
  if (!r.ok) throw new Error('fetch failed');
  const body = await r.json() as { ciphertext: string };
  return decryptJSON<Summary>(passcode, body.ciphertext);
}

function showGate(): void {
  const gate = document.querySelector('hisaab-gate');
  const app = document.getElementById('app');
  if (gate) gate.removeAttribute('hidden');
  if (app) app.hidden = true;
}

function revealApp(): void {
  const gate = document.querySelector('hisaab-gate');
  const app = document.getElementById('app');
  if (gate) gate.setAttribute('hidden', '');
  if (app) app.hidden = false;
}

function refreshHero(s: Summary): void {
  const total = document.getElementById('total');
  const sub = document.getElementById('hero-sub');
  if (total) total.textContent = formatINR(s.totals.inr);
  if (sub) sub.textContent = `${s.transaction_count} transactions · last 90 days`;
}

function refreshMarginalia(s: Summary): void {
  const box = document.getElementById('marginalia');
  if (!box) return;
  // Pull top-3 highest-amount transactions as editorial whispers
  const top3 = [...s.transactions]
    .sort((a, b) => b.amount_inr - a.amount_inr)
    .slice(0, 3);
  if (top3.length === 0) {
    box.innerHTML = '';
    return;
  }
  box.innerHTML = top3.map((tx) => whisper(tx)).join('');
}

function whisper(tx: Transaction): string {
  const merch = MERCHANT_DISPLAY[tx.merchant_id] ?? tx.merchant_id;
  const date = formatShort(tx.date);
  const items = tx.items[0] ?? 'item';
  return `<div class="whisper">
    <span class="mark">${date}</span>
    <b>${formatINR(tx.amount_inr)}</b> went to <i>${escapeHtml(merch)}</i> for <i>${escapeHtml(items)}</i>.
  </div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function formatShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

store.subscribe((s) => {
  if (s.phase === 'ready' && s.summary) {
    revealApp();
    refreshHero(s.summary);
    refreshMarginalia(s.summary);
  }
});

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* ignore */ });
  });
}

bootstrap();
import { mountAmbient as mountEmpireSelf } from './agent/hook.ts';
queueMicrotask(() => mountEmpireSelf());
